import axios from 'axios';
import { API_BASE_URL as BASE_URL } from '../../config/api';

const API_BASE_URL = `${BASE_URL}/api/v1/auth`;

// refresh 전용 axios 인스턴스 (인터셉터 없음)
const refreshAxios = axios.create();

// 토큰 갱신 중 여부를 추적하는 플래그
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// refresh API 함수 (별도 인스턴스 사용)
export const refresh = async (refreshToken) => {
  const response = await refreshAxios.post(`${API_BASE_URL}/refresh`, {
    refreshToken: refreshToken
  });
  return response.data;
};

// axios 응답 인터셉터 설정
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    console.log('API 에러 발생:');

    // 401 에러일 때만 토큰 갱신 시도
    if (error.response?.status === 401 && !originalRequest._retry) {
      // auth 관련 API는 토큰 갱신 시도하지 않음
      if (originalRequest.url?.includes('/api/v1/auth/')) {
        console.log('Auth API 에러 - 토큰 갱신 시도하지 않음');
        return Promise.reject(error);
      }

      // 토큰이 없으면 갱신 시도하지 않음
      const hasTokens = localStorage.getItem('token') && localStorage.getItem('refreshToken');
      if (!hasTokens) {
        console.log('토큰이 없어서 갱신 시도하지 않음');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        console.log('이미 토큰 갱신 중 - 큐에 추가');
        // 이미 토큰 갱신 중이면 큐에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // 리프레시 토큰이 없으면 로그아웃
        console.log('리프레시 토큰이 없음 - 로그아웃 처리');
        handleLogout();
        return Promise.reject(error);
      }

      try {
        console.log('토큰 갱신 시도 중... (CORS/Network 에러로 인한 갱신)');
        const response = await refresh(refreshToken);
        const newAccessToken = response.accessToken;
        const newRefreshToken = response.refreshToken;

        console.log('토큰 갱신 성공 - 새 토큰으로 요청 재시도');

        // 새 토큰들 저장
        localStorage.setItem('token', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // axios 기본 헤더 업데이트
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;

        // 대기 중인 요청들 처리
        processQueue(null, newAccessToken);

        // 원래 요청 재시도
        originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        console.log('원래 요청 재시도:', originalRequest.url);
        return axios(originalRequest);

      } catch (refreshError) {
        // 리프레시 토큰도 만료된 경우
        console.log('토큰 갱신 실패 - 로그아웃 처리', refreshError.response?.status, refreshError.message);
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// 로그아웃 처리 함수
const handleLogout = () => {
  console.log('로그아웃 처리 실행');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');

  // authStore에서 logout 호출
  if (window.authStore) {
    window.authStore.logout();
  }

  // 로그인 페이지로 리다이렉트 (React Router 사용 시)
  if (window.location.pathname !== '/login') {
    console.log('로그인 페이지로 리다이렉트');
    window.location.href = '/login';
  }
};

// axios 요청 인터셉터 설정 (토큰이 필요한 API에만 토큰 자동 첨부)
axios.interceptors.request.use(
  (config) => {
    // 토큰이 필요 없는 API 목록
    const authEndpoints = [
      '/api/v1/auth/check-name-duplicated',
      '/api/v1/auth/signup',
      '/api/v1/auth/login',
      '/api/v1/auth/login/admin',
      '/api/v1/auth/kakao',
      '/api/v1/auth/kakao/complete',
      '/api/v1/auth/phone/send-verification-code',
      '/api/v1/auth/phone/verify-verification-code',
      '/api/v1/auth/refresh',
      '/api/v1/auth/logout',
      '/api/v1/auth/reset-password',
    ];

    const isAuthEndpoint = authEndpoints.some(endpoint =>
      config.url?.includes(endpoint)
    );

    // 인증이 필요 없는 엔드포인트가 아닌 경우에만 토큰 첨부
    if (!isAuthEndpoint) {
      const token = localStorage.getItem('token');
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const signup = async (name, phone, password) => {
  const response = await axios.post(`${API_BASE_URL}/signup`, {
    name: name,
    phone: phone,
    password: password
  });
  return response.data;
};

export const login = async (phone, password) => {
  // RN 앱에서 주입된 FCM 토큰 가져오기 (있으면 사용, 없으면 null)
  const fcmToken = window.__FCM_TOKEN__ || null;
  console.log('로그인 시 FCM 토큰:', fcmToken);

  const response = await axios.post(`${API_BASE_URL}/login`, {
    phone: phone,
    password: password,
    fcmToken: fcmToken
  });
  return response.data;
};

export const adminLogin = async (name, password1, password2, code) => {
  const response = await axios.post(`${API_BASE_URL}/login/admin`, {
    name: name,
    password1: password1,
    password2: password2,
    code: code
  });
  return response.data;
};

export const logout_server = async () => {
  const token = localStorage.getItem('token');
  const response = await axios.post(`${API_BASE_URL}/logout`, {}, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response;
};

export const sendVerificationCode = async (phone) => {
  const response = await axios.post(`${API_BASE_URL}/phone/send-verification-code`, {
    phone: phone
  });
  return response.data;
};

export const verifyVerificationCode = async (phone, verificationCode) => {
  const response = await axios.post(`${API_BASE_URL}/phone/verify-verification-code`, {
    phone: phone,
    verificationCode: verificationCode
  });
  return response.data;
};

// 카카오 로그인 관련 API
export const kakaoLogin = async (code) => {
  // RN 앱에서 주입된 FCM 토큰 가져오기 (있으면 사용, 없으면 null)
  const fcmToken = window.__FCM_TOKEN__ || null;
  console.log('카카오 로그인 시 FCM 토큰:', fcmToken);

  const response = await axios.post(`${API_BASE_URL}/kakao`, {
    code: code,
    fcmToken: fcmToken
  });
  return response.data;
};

export const kakaoComplete = async (name, phone, providerId) => {
  // RN 앱에서 주입된 FCM 토큰 가져오기 (있으면 사용, 없으면 null)
  const fcmToken = window.__FCM_TOKEN__ || null;
  console.log('카카오 회원가입 완료 시 FCM 토큰:', fcmToken);

  const response = await axios.post(`${API_BASE_URL}/kakao/complete`, {
    name: name,
    phone: phone,
    providerId: providerId,
    fcmToken: fcmToken
  });
  return response.data;
};

// 비밀번호 재설정 API
export const resetPassword = async (phone, newPassword) => {
  const response = await axios.patch(`${API_BASE_URL}/reset-password`, {
    phone: phone,
    newPassword: newPassword
  });
  return response.data;
};

export const checkNameDuplicated = async (name) => {
  const response = await axios.get(`${API_BASE_URL}/check-name-duplicated`, {
    params: { name: name }
  });
  return response.data;
};
